/**
 * HomeReference — reference-image match homepage for Anabiya Decor.
 *
 * Sections (all editable via /admin → Homepage tab):
 *   1. SplitHero (text left · bedroom photo right)                      → hero
 *   2. TrustStrip (5 badges)                                            → trust_bar
 *   3. CategoryTilesGrid (2 rows of category tiles)                     → categories_showcase
 *   4. StatsRow + Trade Catalogue CTA                                   → stats + catalogue_cta
 *   5. StoryPreview → "Know more about us" links to /about              → brand_story
 *   6. QualityValues (4 icons: team / manufacturing / global / focus)   → quality_values
 *   7. Leadership (2 leader cards)                                      → leadership
 *   8. B2B Wholesale CTA bar                                            → catalogue_cta
 *   9. Newsletter                                                       → newsletter
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Crown,
  Users,
  Factory,
  Globe,
  Target,
  Award,
  Handshake,
  Truck,
  Sparkles,
  Building2,
} from "lucide-react";

import { NavbarLuxe } from "@/components/NavbarLuxe";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Footer } from "@/components/Footer";
import { FloatingButtons } from "@/components/FloatingButtons";
import { Seo } from "@/components/Seo";
import { productAltText } from "@/lib/seoDerive";
import { useHomeContent } from "@/context/HomeContentContext";
import { useSmoothScroll } from "@/hooks/useSmoothScroll";
import { CATEGORIES, PRODUCTS } from "@/data/anabiya";
import { useEffectiveCategories, useEffectiveProducts, resolveImage } from "@/context/PricesContext";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Placeholder photos for defaults (all overridable). */
const IMG = {
  hero: "https://images.unsplash.com/photo-1631049035182-249067d7618e?auto=format&fit=crop&w=1600&q=80",
  story: "https://images.unsplash.com/photo-1615874959474-d609969a20ed?auto=format&fit=crop&w=1400&q=80",
  values: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=80",
  ceo: "https://images.unsplash.com/photo-1531891437562-4301cf35b7e4?auto=format&fit=crop&w=800&q=80",
  designer: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=800&q=80",
};

const CATEGORY_TILE_DEFAULTS = [
  { label: "Duvet Covers",     slug: "duvet-covers",     image: "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?auto=format&fit=crop&w=900&q=80" },
  { label: "Sheet Sets",       slug: "sheet-sets",       image: "https://images.unsplash.com/photo-1631049035182-249067d7618e?auto=format&fit=crop&w=900&q=80" },
  { label: "Curtains",         slug: "curtains",         image: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=900&q=80" },
  { label: "Bed Skirts",       slug: "bed-skirts",       image: "https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?auto=format&fit=crop&w=900&q=80" },
  { label: "Headboards",       slug: "headboards",       image: "https://images.unsplash.com/photo-1631679706909-1844bbd07221?auto=format&fit=crop&w=900&q=80" },
  { label: "Bath Towels",      slug: "bath-towels",      image: "https://images.unsplash.com/photo-1620626011761-996317b8d101?auto=format&fit=crop&w=900&q=80" },
  { label: "Bathrobes",        slug: "bathrobes",        image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=900&q=80" },
  { label: "Cushion Covers",   slug: "cushion-covers",   image: "https://images.unsplash.com/photo-1584100936595-c0654b55a2e2?auto=format&fit=crop&w=900&q=80" },
  { label: "Winter Jackets",   slug: "winter-jackets",   image: "https://images.unsplash.com/photo-1544022613-e87ca75a784a?auto=format&fit=crop&w=900&q=80" },
  { label: "Ladies Dresses",   slug: "linen-dresses",    image: "https://images.unsplash.com/photo-1595777457583-95e059d581b8?auto=format&fit=crop&w=900&q=80" },
  { label: "Handmade Items",   slug: "handmade-gifts",   image: "https://images.unsplash.com/photo-1512909006721-3d6018887383?auto=format&fit=crop&w=900&q=80" },
  { label: "Windows & Blinds", slug: "windows",          image: "https://images.unsplash.com/photo-1615529162924-f8605388461d?auto=format&fit=crop&w=900&q=80" },
];

/* ---------- 1. Split Hero (D2C-first) — with optional slider ---------- */
const SplitHero = ({ onOpenTrade }) => {
  const { getSection } = useHomeContent();
  const h = getSection("hero", {
    kicker: "LUXURY FOR THE WAY YOU LIVE",
    title: "Luxury Bedding,",
    title_accent: "Crafted for Beautiful Homes.",
    subtitle: "Premium duvet covers, sheet sets, quilts and home textiles designed with timeless elegance. Shipped worldwide from Mumbai.",
    cta_primary: "Shop Bedding",
    cta_secondary: "Explore Bestsellers",
    image: IMG.hero,
  });

  const HERO_FALLBACK = {
    kicker: "LUXURY FOR THE WAY YOU LIVE",
    title: "Luxury Bedding,",
    title_accent: "Crafted for Beautiful Homes.",
    subtitle: "Premium duvet covers, sheet sets, quilts and home textiles designed with timeless elegance. Shipped worldwide from Mumbai.",
    cta_primary: "Shop Bedding",
    cta_secondary: "Explore Bestsellers",
  };
  // Build the list of slides. If `slides` array is present, use it; otherwise
  // fall back to the single legacy `image` + top-level copy for backwards compat.
  const rawSlides = Array.isArray(h.slides) ? h.slides.filter((s) => s && (s.image || s.title || s.kicker)) : [];
  const pickCopy = (slideVal, heroVal, fb) => (slideVal ?? heroVal) || fb;
  const slides = rawSlides.length > 0 ? rawSlides.map((s) => ({
    image: s.image || h.image || IMG.hero,
    kicker: pickCopy(s.kicker, h.kicker, HERO_FALLBACK.kicker),
    title: pickCopy(s.title, h.title, HERO_FALLBACK.title),
    title_accent: pickCopy(s.title_accent, h.title_accent, HERO_FALLBACK.title_accent),
    subtitle: pickCopy(s.subtitle, h.subtitle, HERO_FALLBACK.subtitle),
    cta_primary: pickCopy(s.cta_primary, h.cta_primary, HERO_FALLBACK.cta_primary),
    cta_primary_link: s.cta_primary_link || "/shop?category=duvet-covers",
    cta_secondary: pickCopy(s.cta_secondary, h.cta_secondary, HERO_FALLBACK.cta_secondary),
    cta_secondary_link: s.cta_secondary_link || "#bestsellers",
  })) : [{
    image: h.image || IMG.hero,
    kicker: h.kicker || HERO_FALLBACK.kicker,
    title: h.title || HERO_FALLBACK.title,
    title_accent: h.title_accent || HERO_FALLBACK.title_accent,
    subtitle: h.subtitle || HERO_FALLBACK.subtitle,
    cta_primary: h.cta_primary || HERO_FALLBACK.cta_primary,
    cta_primary_link: "/shop?category=duvet-covers",
    cta_secondary: h.cta_secondary || HERO_FALLBACK.cta_secondary,
    cta_secondary_link: "#bestsellers",
  }];

  const [idx, setIdx] = useState(0);
  const total = slides.length;
  const cur = slides[idx % total];
  // Descriptive, real-copy-driven ALT for the hero image (slide title when set).
  const heroAlt = (() => {
    const t = [cur?.title, cur?.title_accent].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
    return t ? `${t} — Anabiya Decor` : "Luxury bedding and home textiles by Anabiya Decor";
  })();

  // Auto-advance
  useEffect(() => {
    if (total < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 6000);
    return () => clearInterval(t);
  }, [total]);

  const goPrev = () => setIdx((i) => (i - 1 + total) % total);
  const goNext = () => setIdx((i) => (i + 1) % total);

  const isInternalLink = (link) => typeof link === "string" && link.startsWith("/");
  const isAnchor = (link) => typeof link === "string" && link.startsWith("#");

  const renderCta = (label, link, primary) => {
    if (!label) return null;
    const cls = primary
      ? "inline-flex items-center gap-2 md:gap-3 bg-gold text-forest overline text-[11px] md:text-xs px-6 md:px-8 py-3.5 md:py-4 hover:bg-champagne transition-colors duration-500 tracking-[2px] md:tracking-[3px]"
      : "inline-flex items-center gap-2 md:gap-3 border border-champagne/60 text-champagne overline text-[11px] md:text-xs px-6 md:px-8 py-3.5 md:py-4 hover:bg-champagne hover:text-forest transition-colors duration-500 tracking-[2px] md:tracking-[3px]";
    const testid = primary ? "hero-cta-shop" : "hero-cta-bestsellers";
    if (isInternalLink(link)) {
      return (
        <Link to={link} data-testid={testid} className={cls}>
          {label} <ArrowRight size={13} strokeWidth={1.8} />
        </Link>
      );
    }
    return (
      <a href={link || "#"} data-testid={testid} className={cls}>
        {label} <ArrowRight size={13} strokeWidth={1.8} />
      </a>
    );
  };

  // Which layout? "full" is the default cinematic layout; "split" keeps the old 2-col.
  const layout = (h.layout || "full") === "split" ? "split" : "full";

  const SliderControls = () => (
    <>
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={goPrev}
            aria-label="Previous slide"
            data-testid="hero-prev"
            className="absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full border border-champagne/40 bg-forest/40 backdrop-blur-md text-champagne hover:bg-forest/70 hover:border-champagne transition-colors flex items-center justify-center z-20"
          >
            <ChevronLeft size={16} strokeWidth={1.8} />
          </button>
          <button
            type="button"
            onClick={goNext}
            aria-label="Next slide"
            data-testid="hero-next"
            className="absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full border border-champagne/40 bg-forest/40 backdrop-blur-md text-champagne hover:bg-forest/70 hover:border-champagne transition-colors flex items-center justify-center z-20"
          >
            <ChevronRight size={16} strokeWidth={1.8} />
          </button>
          <div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20"
            data-testid="hero-dots"
          >
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`hero-dot-${i}`}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  i === idx ? "w-8 bg-gold" : "w-4 bg-champagne/40 hover:bg-champagne/70"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </>
  );

  // ----------------- FULL HERO ----------------- //
  if (layout === "full") {
    // Text position on the full hero: left / center / right (default center)
    const align = h.text_align || "center";
    const alignBox = align === "left" ? "items-start text-left" : align === "right" ? "items-end text-right" : "items-center text-center";
    const overlay = h.overlay ?? 0.5; // 0 = no dim, 1 = solid
    return (
      <section
        data-testid="hero-luxe"
        className="text-champagne relative overflow-hidden"
        style={{ backgroundColor: h.bg_color || undefined }}
      >
        <div className="relative min-h-[80vh] md:min-h-[88vh] w-full">
          {/* Full-bleed image slider */}
          <AnimatePresence mode="sync">
            <motion.img
              key={`img-${idx}`}
              src={cur.image || IMG.hero}
              alt={heroAlt}
              fetchpriority="high"
              loading="eager"
              decoding="async"
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.02 }}
              transition={{ duration: 1.2, ease: [0.7, 0, 0.2, 1] }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </AnimatePresence>
          {/* Legibility overlay */}
          <div
            className="absolute inset-0 bg-forest pointer-events-none"
            style={{ opacity: Math.max(0, Math.min(1, Number(overlay))) }}
          />
          {/* Subtle bottom vignette so CTAs read against bright shots */}
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-forest/70 via-forest/20 to-transparent pointer-events-none" />

          {/* Overlaid content */}
          <div className={`relative z-10 min-h-[78vh] md:min-h-[88vh] w-full flex flex-col justify-center px-5 md:px-14 lg:px-20 py-14 md:py-24 lg:py-28 ${alignBox}`}>
            <AnimatePresence mode="wait">
              <motion.div
                key={`text-full-${idx}`}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.7, ease: [0.7, 0, 0.2, 1] }}
                className="max-w-3xl w-full"
              >
                {cur.kicker && (
                  <div className="overline text-gold mb-4 md:mb-6 tracking-[3px] md:tracking-[4px] text-[10px] md:text-[11px]" data-testid="hero-kicker">
                    {cur.kicker}
                  </div>
                )}
                <h1
                  className="font-display text-champagne text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-[1.02] tracking-[-0.02em]"
                  data-testid="hero-headline"
                >
                  {cur.title}
                  {cur.title_accent && (
                    <>
                      <br />
                      <span className="italic font-editorial text-gold">{cur.title_accent}</span>
                    </>
                  )}
                </h1>
                {cur.subtitle && (
                  <p className={`mt-5 md:mt-8 text-champagne/85 text-sm md:text-lg lg:text-xl leading-relaxed font-light ${align === "center" ? "mx-auto" : ""} max-w-2xl`}>
                    {cur.subtitle}
                  </p>
                )}
                <div className={`mt-7 md:mt-10 flex flex-wrap gap-2.5 md:gap-3 ${align === "center" ? "justify-center" : align === "right" ? "justify-end" : ""}`}>
                  {renderCta(cur.cta_primary, cur.cta_primary_link, true)}
                  {renderCta(cur.cta_secondary, cur.cta_secondary_link, false)}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <SliderControls />
        </div>
      </section>
    );
  }

  // ----------------- SPLIT HERO (original) ----------------- //
  return (
    <section
      data-testid="hero-luxe"
      className="text-champagne relative overflow-hidden"
      style={{ backgroundColor: h.bg_color || undefined }}
    >
      <div className={`grid grid-cols-1 lg:grid-cols-12 min-h-[80vh] ${h.bg_color ? "" : "bg-forest"}`}>
        {/* Text side — animates on slide change */}
        <div className="lg:col-span-6 px-6 md:px-14 lg:px-20 py-16 lg:py-28 flex flex-col justify-center relative z-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={`text-${idx}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.6, ease: [0.7, 0, 0.2, 1] }}
              className="max-w-xl"
            >
              {cur.kicker && (
                <div className="overline text-gold mb-6 tracking-[4px] text-[11px]" data-testid="hero-kicker">
                  {cur.kicker}
                </div>
              )}
              <h1
                className="font-display text-champagne text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.02em]"
                data-testid="hero-headline"
              >
                {cur.title}
                {cur.title_accent && (
                  <>
                    <br />
                    <span className="italic font-editorial text-gold">{cur.title_accent}</span>
                  </>
                )}
              </h1>
              {cur.subtitle && (
                <p className="mt-8 text-champagne/70 text-base md:text-lg leading-relaxed font-light max-w-lg">
                  {cur.subtitle}
                </p>
              )}
              <div className="mt-10 flex flex-wrap gap-3">
                {renderCta(cur.cta_primary, cur.cta_primary_link, true)}
                {renderCta(cur.cta_secondary, cur.cta_secondary_link, false)}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Image side — crossfade slider */}
        <div className="lg:col-span-6 relative min-h-[420px] lg:min-h-[80vh] bg-forest">
          <AnimatePresence mode="sync">
            <motion.img
              key={`img-${idx}`}
              src={cur.image || IMG.hero}
              alt={heroAlt}
              fetchpriority="high"
              loading="eager"
              decoding="async"
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.02 }}
              transition={{ duration: 1.1, ease: [0.7, 0, 0.2, 1] }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-forest/40 pointer-events-none" />

          <SliderControls />
        </div>
      </div>
    </section>
  );
};

/* ---------- 2. Trust Strip (only genuine claims) ---------- */
const DEFAULT_TRUST = [
  { icon: "Award", label: "Premium Materials", body: "Finest fabrics & craftsmanship" },
  { icon: "Sparkles", label: "Crafted in India", body: "Designed and made in Mumbai" },
  { icon: "Handshake", label: "Custom Made", body: "Bespoke sizes & designs" },
  { icon: "Truck", label: "International Shipping", body: "Delivered worldwide" },
  { icon: "Crown", label: "Secure Checkout", body: "Trusted payment gateway" },
];
const TRUST_ICONS = { Award, Crown, Sparkles, Handshake, Truck, Globe, Users, Factory, Target, Building2 };

const TrustStrip = () => {
  const { getSection } = useHomeContent();
  const cfg = getSection("trust_bar", { items: DEFAULT_TRUST });
  const items = Array.isArray(cfg.items) && cfg.items.length
    ? cfg.items.map((it, i) => (typeof it === "string" ? { icon: DEFAULT_TRUST[i]?.icon || "Award", label: it, body: DEFAULT_TRUST[i]?.body || "" } : it))
    : DEFAULT_TRUST;

  return (
    <section data-testid="trust-strip" className="bg-cream border-y border-hairline">
      <div className="max-w-7xl mx-auto px-6 md:px-14 lg:px-20 py-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
        {items.slice(0, 5).map((it, i) => {
          const Icon = TRUST_ICONS[it.icon] || Award;
          return (
            <div key={i} className="flex flex-col items-center text-center" data-testid={`trust-item-${i}`}>
              <Icon size={28} strokeWidth={1.4} className="text-gold mb-3" />
              <div className="font-display text-forest text-lg leading-tight">{it.label}</div>
              {it.body && <div className="text-forest/55 text-xs mt-1 font-light">{it.body}</div>}
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* ---------- 2b. Bestsellers (D2C conversion booster) ---------- */
const BESTSELLER_CATS = ["duvet-covers", "sheet-sets", "quilts-bedspreads", "comforters", "decorative-cushions", "pillow-covers", "cushion-covers", "curtains"];

const Bestsellers = () => {
  const products = useEffectiveProducts(PRODUCTS);
  const { addItem } = useCart();
  const { format } = useCurrency();
  const { getSection } = useHomeContent();
  const cfg = getSection("bestsellers", {
    kicker: "THE ANABIYA EDIT",
    headline: "Our most-loved pieces",
    headline_accent: " for beautifully considered spaces.",
    cta: "Shop all bestsellers",
  });

  // Prioritise: featured first, then products in bestseller categories, capped at 8
  const picks = (() => {
    const seen = new Set();
    const list = [];
    // Featured first
    products.filter((p) => p.featured && !seen.has(p.slug)).forEach((p) => { seen.add(p.slug); list.push(p); });
    // Then bestseller categories
    for (const cat of BESTSELLER_CATS) {
      const inCat = products.filter((p) => p.category === cat && !seen.has(p.slug)).slice(0, 2);
      inCat.forEach((p) => { seen.add(p.slug); list.push(p); });
      if (list.length >= 8) break;
    }
    return list.slice(0, 8);
  })();

  if (picks.length === 0) return null;

  return (
    <section id="bestsellers" data-testid="bestsellers" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-14 lg:px-20">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="overline text-gold tracking-[4px] text-[11px] mb-3">{cfg.kicker}</div>
          <h2 className="font-display text-forest text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            {cfg.headline}<span className="italic font-editorial text-champagne">{cfg.headline_accent}</span>
          </h2>
        </div>
        <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
          {picks.map((p) => {
            const soldOut = typeof p.stock === "number" && p.stock <= 0;
            return (
              <li key={p.slug} data-testid={`bestseller-${p.slug}`} className="group">
                <Link to={`/product/${p.slug}`} className="block">
                  <div className="relative aspect-[4/5] overflow-hidden bg-beige">
                    <img
                      src={resolveImage(p.images[0])}
                      alt={productAltText(p)}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.7,0,0.2,1)] group-hover:scale-[1.05]"
                    />
                    {p.tag && !soldOut && (
                      <span className="absolute top-3 left-3 overline bg-gold text-forest px-2 py-1 rounded-full text-[10px]">{p.tag}</span>
                    )}
                    {soldOut && (
                      <span className="absolute top-3 left-3 overline bg-forest text-white px-2 py-1 rounded-full text-[10px]">Sold out</span>
                    )}
                  </div>
                </Link>
                <div className="mt-4">
                  <Link to={`/product/${p.slug}`} className="font-display text-forest text-lg md:text-xl leading-tight hover:text-gold transition-colors">
                    {p.title}
                  </Link>
                  {p.caption && <p className="text-forest/55 text-xs mt-1 font-light">{p.caption}</p>}
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="font-display text-forest text-lg tabnum">{format(p.price)}</span>
                    {p.mrp && p.mrp > p.price && (
                      <span className="text-forest/40 line-through tabnum text-xs">{format(p.mrp)}</span>
                    )}
                  </div>
                  <button
                    onClick={() => addItem({
                      slug: p.slug,
                      title: p.title,
                      price: p.price,
                      image: resolveImage(p.images[0]),
                      size: p.sizes?.[0] || "",
                      colour: p.colours?.[0]?.name || "",
                      qty: 1,
                    })}
                    disabled={soldOut}
                    data-testid={`bestseller-add-${p.slug}`}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-forest text-forest overline text-xs px-3 py-2.5 hover:bg-forest hover:text-champagne transition-colors tracking-[2px] disabled:opacity-40"
                  >
                    {soldOut ? "Sold out" : "Add to Cart"}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
        <div className="mt-14 text-center">
          <Link
            to="/shop"
            data-testid="bestsellers-view-all"
            className="inline-flex items-center gap-3 bg-forest text-champagne overline text-xs px-8 py-4 hover:bg-[hsl(150_60%_16%)] transition-colors tracking-[3px]"
          >
            {cfg.cta || "Shop all bestsellers"} <ArrowRight size={13} strokeWidth={1.8} />
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ---------- 3. Category Tiles Grid ---------- */
const CategoryTilesGrid = () => {
  const { getSection } = useHomeContent();
  const cats = useEffectiveCategories(CATEGORIES);
  const cfg = getSection("categories_showcase", {
    kicker: "SHOP BY CATEGORY",
    headline: "Explore Our",
    headline_accent: "Collections.",
  });
  const kicker = cfg.kicker || "SHOP BY CATEGORY";
  const headline = cfg.headline || "Explore Our";
  const headlineAccent = cfg.headline_accent || "Collections.";
  // Robust tile source — always guarantee 12 tiles even if CMS overrides are malformed.
  const cmsItems = Array.isArray(cfg.items) ? cfg.items.filter((t) => t && t.slug) : [];
  const fallbackImg = (i) => CATEGORY_TILE_DEFAULTS[i % CATEGORY_TILE_DEFAULTS.length].image;
  const source = cmsItems.length > 0 ? cmsItems : CATEGORY_TILE_DEFAULTS;
  const custom = source.map((t, i) => {
    const merged = cats.find((c) => c.slug === t.slug);
    const defTile = CATEGORY_TILE_DEFAULTS.find((d) => d.slug === t.slug) || CATEGORY_TILE_DEFAULTS[i % CATEGORY_TILE_DEFAULTS.length];
    return {
      label: t.label || merged?.label || defTile.label || t.slug,
      slug: t.slug,
      image: t.image || merged?.image || defTile.image || fallbackImg(i),
    };
  });

  return (
    <section id="collections" data-testid="collections-section" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-14 lg:px-20">
        <div className="text-center mb-14">
          <div className="overline text-gold tracking-[4px] text-[11px] mb-3" data-testid="catgrid-kicker">
            {kicker}
          </div>
          <h2 className="font-display text-forest text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            {headline} <span className="italic font-editorial text-champagne">{headlineAccent}</span>
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
          {custom.slice(0, 12).map((c, i) => (
            <Link
              key={c.slug + i}
              to={`/shop?category=${c.slug}`}
              data-testid={`collection-${c.slug}`}
              className="group block"
            >
              <div className="relative aspect-[4/5] md:aspect-square overflow-hidden bg-beige">
                <img
                  src={c.image}
                  alt={`${c.label} — shop the collection at Anabiya Decor`}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.src = CATEGORY_TILE_DEFAULTS[0].image; }}
                  className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.7,0,0.2,1)] group-hover:scale-[1.06]"
                />
                {/* Stronger scrim for mobile legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10 md:from-black/70 md:via-black/10 md:to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-3 md:p-5">
                  <div className="font-display text-champagne text-lg md:text-2xl leading-tight drop-shadow-md">{c.label}</div>
                  <div className="mt-1 overline text-champagne/80 text-[9px] md:text-[10px] tracking-[3px] inline-flex items-center gap-1">
                    Explore <ArrowRight size={10} strokeWidth={1.8} />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/shop"
            data-testid="catgrid-view-all"
            className="inline-flex items-center gap-3 border border-forest text-forest overline text-xs px-8 py-4 hover:bg-forest hover:text-champagne transition-colors duration-500 tracking-[3px]"
          >
            View All Products <ArrowRight size={13} strokeWidth={1.8} />
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ---------- 4. Stats Row + Trade CTA (moved lower, D2C-lite) ---------- */
const DEFAULT_STATS = [
  { number: "Mumbai", label: "Design & Craft Studio" },
  { number: "Worldwide", label: "International Shipping" },
  { number: "Bespoke", label: "Custom Made Solutions" },
  { number: "Trade", label: "Wholesale Enquiries Welcome" },
];

const StatsRow = ({ onOpenTrade }) => {
  const { getSection } = useHomeContent();
  const cfg = getSection("stats", { items: DEFAULT_STATS, cta_text: "Request Trade Catalogue" });
  const items = Array.isArray(cfg.items) && cfg.items.length ? cfg.items : DEFAULT_STATS;

  return (
    <section data-testid="stats-row" className="bg-forest text-champagne py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-6 md:px-14 lg:px-20 grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
        <div className="lg:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-4">
          {items.slice(0, 4).map((s, i) => (
            <div key={i} data-testid={`stat-${i}`} className="min-w-0">
              <div className="font-display text-gold text-3xl sm:text-4xl md:text-5xl lg:text-6xl tabnum leading-none break-words">{s.number}</div>
              <div className="overline text-champagne/60 mt-3 text-[10px] tracking-[3px]">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="lg:col-span-1 flex lg:justify-end">
          <button
            onClick={onOpenTrade}
            data-testid="stats-cta-trade"
            className="inline-flex items-center gap-3 bg-gold text-forest overline text-xs px-6 py-4 hover:bg-champagne transition-colors duration-500 tracking-[3px]"
          >
            {cfg.cta_text || "Request Trade Catalogue"} <ArrowRight size={13} strokeWidth={1.8} />
          </button>
        </div>
      </div>
    </section>
  );
};

/* ---------- 5. Story Preview ---------- */
const StoryPreview = () => {
  const { getSection } = useHomeContent();
  const cfg = getSection("brand_story", {
    kicker: "OUR STORY",
    headline: "A Legacy of",
    headline_accent: " Craftsmanship & Quality.",
    body: "Anabiya Decor was founded in 2016 in the heart of Mumbai with a simple vision — to create premium home textiles that blend timeless elegance, superior quality and exceptional comfort. Today, our collections are trusted by hotels, resorts, retailers and interior designers across 30+ countries.",
    cta: "Know More About Us",
    image: IMG.story,
  });

  return (
    <section id="our-journey" data-testid="story-preview" className="py-20 md:py-28 bg-cream">
      <div className="max-w-7xl mx-auto px-6 md:px-14 lg:px-20 grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14 items-center">
        <div className="lg:col-span-6">
          <div className="aspect-[4/5] overflow-hidden bg-beige">
            <img
              src={cfg.image || IMG.story}
              alt="Anabiya Decor — legacy of craftsmanship in Indian textiles"
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="overline text-gold tracking-[4px] text-[11px] mb-3">{cfg.kicker}</div>
          <h2 className="font-display text-forest text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            {cfg.headline}
            <span className="italic font-editorial text-champagne">{cfg.headline_accent}</span>
          </h2>
          <p className="mt-6 text-forest/70 text-base md:text-lg leading-relaxed font-light">
            {cfg.body}
          </p>
          <Link
            to="/about"
            data-testid="brand-story-cta"
            className="mt-10 inline-flex items-center gap-3 bg-forest text-champagne overline text-xs px-8 py-4 hover:bg-[hsl(150_60%_16%)] transition-colors duration-500 tracking-[3px]"
          >
            {cfg.cta} <ArrowRight size={13} strokeWidth={1.8} />
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ---------- 6. Quality Values (4 icons) ---------- */
const DEFAULT_VALUES = [
  { icon: "Users", title: "Experienced Team", body: "Skilled artisans and craftsmanship professionals with decades of textile heritage." },
  { icon: "Factory", title: "Advanced Manufacturing", body: "Modern facilities with strict quality control and premium fabric sourcing." },
  { icon: "Globe", title: "Global Standards", body: "International quality certifications and export compliance across 30+ countries." },
  { icon: "Target", title: "Client Focused", body: "Your success is our priority — flexible MOQ, custom sizes, private labelling." },
];

const QualityValues = () => {
  const { getSection } = useHomeContent();
  const cfg = getSection("quality_values", {
    kicker: "ABOUT OUR COMPANY",
    headline: "Where Tradition Meets",
    headline_accent: " Innovation.",
    items: DEFAULT_VALUES,
    image: IMG.values,
  });
  const items = Array.isArray(cfg.items) && cfg.items.length ? cfg.items : DEFAULT_VALUES;

  return (
    <section data-testid="quality-values" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-14 lg:px-20">
        <div className="max-w-3xl">
          <div className="overline text-gold tracking-[4px] text-[11px] mb-3">{cfg.kicker}</div>
          <h2 className="font-display text-forest text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            {cfg.headline}
            <span className="italic font-editorial text-champagne">{cfg.headline_accent}</span>
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
          {items.slice(0, 4).map((v, i) => {
            const Icon = TRUST_ICONS[v.icon] || Users;
            return (
              <div key={i} data-testid={`value-${i}`} className="flex flex-col items-start">
                <div className="w-14 h-14 flex items-center justify-center bg-forest text-gold rounded-full mb-5">
                  <Icon size={22} strokeWidth={1.4} />
                </div>
                <div className="font-display text-forest text-xl leading-tight">{v.title}</div>
                <div className="text-forest/60 text-sm mt-3 leading-relaxed font-light">{v.body}</div>
              </div>
            );
          })}
        </div>

        {cfg.image && (
          <div className="mt-14 aspect-[16/6] overflow-hidden bg-beige">
            <img src={cfg.image} alt="Anabiya Decor manufacturing facility" className="w-full h-full object-cover" loading="lazy" />
          </div>
        )}
      </div>
    </section>
  );
};

/* ---------- 7. Leadership ---------- */
const DEFAULT_LEADERSHIP = {
  kicker: "LEADERSHIP",
  headline: "Guided by Vision,",
  headline_accent: " Driven by Passion.",
  people: [
    { name: "M.D.S.R.", role: "CEO & Founder", body: "Two decades of textile heritage. Leading Anabiya Decor's mission to serve hospitality and design-led buyers worldwide.", image: IMG.ceo },
    { name: "M.D.S.R.", role: "Head Designer", body: "Curating collections that balance timeless silhouettes with modern craftsmanship — every Anabiya piece begins on her desk.", image: IMG.designer },
  ],
};

const Leadership = () => {
  const { getSection } = useHomeContent();
  const cfg = getSection("leadership", DEFAULT_LEADERSHIP);
  // Deep-merge each person over defaults so partial admin edits (e.g. name only)
  // still show a photo / role / bio.
  const savedPeople = Array.isArray(cfg.people) ? cfg.people : [];
  const people = DEFAULT_LEADERSHIP.people.map((defP, i) => ({
    ...defP,
    ...(savedPeople[i] || {}),
    image: (savedPeople[i] && savedPeople[i].image) || defP.image,
  }));

  return (
    <section data-testid="leadership" className="py-20 md:py-28 bg-cream">
      <div className="max-w-7xl mx-auto px-6 md:px-14 lg:px-20">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="overline text-gold tracking-[4px] text-[11px] mb-3">{cfg.kicker}</div>
          <h2 className="font-display text-forest text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            {cfg.headline}
            <span className="italic font-editorial text-champagne">{cfg.headline_accent}</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {people.slice(0, 2).map((p, i) => (
            <div key={i} data-testid={`leader-${i}`} className="border border-hairline bg-white p-8 md:p-10 flex flex-col md:flex-row gap-6">
              <div className="w-full md:w-40 aspect-square shrink-0 overflow-hidden bg-beige">
                <img
                  src={p.image || DEFAULT_LEADERSHIP.people[i].image}
                  alt={`${p.name || DEFAULT_LEADERSHIP.people[i].name} — ${p.role || DEFAULT_LEADERSHIP.people[i].role}`}
                  onError={(e) => { e.currentTarget.src = DEFAULT_LEADERSHIP.people[i].image; }}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="flex-1">
                <div className="overline text-gold text-[10px] tracking-[3px]">{p.role}</div>
                <div className="font-display text-forest text-2xl md:text-3xl mt-2 leading-none">{p.name}</div>
                <p className="mt-4 text-forest/65 text-sm leading-relaxed font-light">{p.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- 8. B2B CTA Bar ---------- */
const B2BCta = ({ onOpenTrade }) => {
  const { getSection } = useHomeContent();
  const cfg = getSection("catalogue_cta", {
    kicker: "B2B WHOLESALE",
    headline: "Partner With",
    headline_accent: " Anabiya Decor.",
    body: "Serving hotels, retailers, resorts and interior designers across 30+ countries. Container-ready wholesale programmes and custom manufacturing.",
    cta: "Enquire Now",
  });

  return (
    <section data-testid="b2b-cta" className="bg-forest text-champagne py-16 md:py-20 border-t border-champagne/10">
      <div className="max-w-7xl mx-auto px-6 md:px-14 lg:px-20 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        <div>
          <div className="overline text-gold tracking-[4px] text-[11px] mb-3">{cfg.kicker}</div>
          <h2 className="font-display text-champagne text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
            {cfg.headline}
            <span className="italic font-editorial text-gold">{cfg.headline_accent}</span>
          </h2>
          <p className="mt-5 text-champagne/70 text-base leading-relaxed font-light max-w-lg">{cfg.body}</p>
        </div>
        <div className="flex md:justify-end">
          <button
            onClick={onOpenTrade}
            data-testid="b2b-cta-btn"
            className="inline-flex items-center gap-3 bg-gold text-forest overline text-xs px-10 py-5 hover:bg-champagne transition-colors duration-500 tracking-[3px]"
          >
            {cfg.cta} <ArrowRight size={13} strokeWidth={1.8} />
          </button>
        </div>
      </div>
    </section>
  );
};

/* ---------- Happy Customer Reviews (testimonials) ---------- */
const DEFAULT_TESTIMONIALS = [
  { name: "Aisha K.", location: "London, UK", rating: 5, quote: "The duvet cover is even more beautiful in person. The fabric is unbelievably soft and the monogram is exquisite — worth every penny.", avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=200&q=80" },
  { name: "Priya S.", location: "New Jersey, USA", rating: 5, quote: "Anabiya sent me a personal note with the parcel. The sheet set fits my king bed perfectly and my guests keep asking where I got it.", avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=200&q=80" },
  { name: "Amelia R.", location: "Sydney, AU", rating: 5, quote: "Ordered curtains for our beach house. Custom size was perfect and the delivery was 12 days flat. Will order again for the guest suite.", avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=200&q=80" },
  { name: "Farah M.", location: "Dubai, UAE", rating: 5, quote: "I wanted a specific colour to match my palette — they matched Pantone exactly. Beautiful craftsmanship, feels like a designer piece.", avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=200&q=80" },
  { name: "Sara D.", location: "Toronto, CA", rating: 5, quote: "The bathrobe is heavenly. Thicker and softer than the ones at my favourite spa. I've bought three more as gifts.", avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=200&q=80" },
  { name: "Emma L.", location: "Berlin, DE", rating: 5, quote: "As an interior designer I've worked with many linen brands. Anabiya's finish is what separates them — real luxury, no shortcuts.", avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=200&q=80" },
];

const Testimonials = () => {
  const { getSection } = useHomeContent();
  const t = getSection("testimonials", {
    kicker: "LOVED WORLDWIDE",
    headline: "Happy customers,",
    headline_accent: " honest words.",
    items: DEFAULT_TESTIMONIALS,
  });
  const items = Array.isArray(t.items) && t.items.length ? t.items : DEFAULT_TESTIMONIALS;
  return (
    <section id="reviews" data-testid="testimonials-section" className="bg-beige py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-14 lg:px-20">
        <div className="text-center mb-14">
          <div className="overline text-gold tracking-[4px] text-[11px] mb-3">{t.kicker}</div>
          <h2 className="font-display text-forest text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            {t.headline} <span className="italic font-editorial text-champagne">{t.headline_accent}</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {items.slice(0, 6).map((r, i) => (
            <motion.figure
              key={i}
              data-testid={`testimonial-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.7, 0, 0.2, 1] }}
              className="bg-white border border-hairline p-6 md:p-8 flex flex-col gap-4"
            >
              <div className="flex items-center gap-1 text-gold">
                {Array.from({ length: r.rating || 5 }).map((_, s) => (
                  <span key={s} aria-hidden className="text-lg leading-none">★</span>
                ))}
              </div>
              <blockquote className="font-editorial text-forest text-lg md:text-xl leading-snug italic">
                &ldquo;{r.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-auto flex items-center gap-3 pt-4 border-t border-hairline">
                {r.avatar && (
                  <img src={r.avatar} alt={r.name} className="w-11 h-11 rounded-full object-cover border border-hairline" loading="lazy" />
                )}
                <div>
                  <div className="font-display text-forest text-base leading-none">{r.name}</div>
                  <div className="overline text-forest/50 text-[10px] tracking-[2px] mt-1">{r.location}</div>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Courier Partners ---------- */
const DEFAULT_COURIERS = [
  { name: "DHL Express",   note: "Priority international · 5-7 days" },
  { name: "FedEx",         note: "Door-to-door tracking · USA / UK / EU" },
  { name: "UPS Worldwide", note: "Bulk export container support" },
  { name: "Aramex",        note: "Middle East + Gulf · 3-5 days" },
  { name: "India Post EMS",note: "Insured global reach · 60+ countries" },
  { name: "Blue Dart",     note: "Domestic India · next-day metros" },
];

const CourierPartners = () => {
  const { getSection } = useHomeContent();
  const c = getSection("couriers", {
    kicker: "SHIPPING WORLDWIDE",
    headline: "Our",
    headline_accent: " courier partners.",
    body: "We ship insured, tracked, and door-to-door — every parcel signed for on arrival.",
    items: DEFAULT_COURIERS,
  });
  const items = Array.isArray(c.items) && c.items.length ? c.items : DEFAULT_COURIERS;
  return (
    <section id="couriers" data-testid="couriers-section" className="bg-white py-16 md:py-24 border-y border-hairline">
      <div className="max-w-7xl mx-auto px-6 md:px-14 lg:px-20">
        <div className="text-center mb-10">
          <div className="overline text-gold tracking-[4px] text-[11px] mb-3">{c.kicker}</div>
          <h2 className="font-display text-forest text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
            {c.headline} <span className="italic font-editorial text-champagne">{c.headline_accent}</span>
          </h2>
          {c.body && <p className="mt-4 text-forest/60 max-w-2xl mx-auto text-base md:text-lg font-light">{c.body}</p>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
          {items.map((p, i) => (
            <motion.div
              key={p.name + i}
              data-testid={`courier-${i}`}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="flex flex-col items-center text-center border border-hairline bg-cream/40 px-4 py-6 hover:border-forest transition-colors duration-500"
            >
              <div className="w-14 h-14 rounded-full bg-forest/5 border border-hairline flex items-center justify-center mb-3">
                <Truck size={22} strokeWidth={1.3} className="text-forest" />
              </div>
              <div className="font-display text-forest text-lg leading-tight">{p.name}</div>
              {p.note && <div className="mt-1.5 text-forest/50 text-[11px] tracking-wide">{p.note}</div>}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Contact block ---------- */
const ContactBlock = () => {
  const { getSection } = useHomeContent();
  const c = getSection("contact", {
    kicker: "GET IN TOUCH",
    headline: "Design your",
    headline_accent: " dream home with us.",
    body: "Wholesale enquiries, custom sizes, monogrammed sets or one perfect gift — we're on WhatsApp 7 days a week.",
    whatsapp: "919137462428",
    whatsapp_display: "+91 91374 62428",
    email: "anabiyadecorhelp@gmail.com",
    address: "Mumbai, India · Ships worldwide",
    hours: "Mon–Sat · 10 AM – 8 PM IST",
  });
  const waLink = `https://wa.me/${(c.whatsapp || "919137462428").replace(/\D/g, "")}?text=${encodeURIComponent("Hi Anabiya, I'd love to know more about your collection.")}`;
  return (
    <section id="contact" data-testid="contact-section" className="bg-forest text-champagne py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-14 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <div className="overline text-gold tracking-[4px] text-[11px] mb-4">{c.kicker}</div>
            <h2 className="font-display text-champagne text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {c.headline} <span className="italic font-editorial text-gold">{c.headline_accent}</span>
            </h2>
            <p className="mt-5 text-champagne/70 text-base md:text-lg font-light leading-relaxed max-w-lg">{c.body}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                data-testid="contact-whatsapp-btn"
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-gold text-forest overline text-xs px-8 py-4 hover:bg-champagne transition-colors duration-500 tracking-[3px]"
              >
                Chat on WhatsApp <ArrowRight size={13} strokeWidth={1.8} />
              </a>
              <a
                data-testid="contact-email-btn"
                href={`mailto:${c.email}`}
                className="inline-flex items-center gap-3 border border-champagne/60 text-champagne overline text-xs px-8 py-4 hover:bg-champagne hover:text-forest transition-colors duration-500 tracking-[3px]"
              >
                Email Us <ArrowRight size={13} strokeWidth={1.8} />
              </a>
            </div>
          </div>
          <div className="lg:col-span-6 lg:pl-8 lg:border-l lg:border-champagne/15">
            <ul className="space-y-6">
              {[
                { label: "WhatsApp", value: c.whatsapp_display || c.whatsapp, href: waLink },
                { label: "Email", value: c.email, href: `mailto:${c.email}` },
                { label: "Studio", value: c.address, href: null },
                { label: "Hours", value: c.hours, href: null },
              ].filter((row) => row.value).map((row) => (
                <li key={row.label} data-testid={`contact-row-${row.label.toLowerCase()}`} className="flex items-start justify-between gap-6 border-b border-champagne/10 pb-6">
                  <div className="overline text-gold text-[10px] tracking-[4px]">{row.label}</div>
                  {row.href ? (
                    <a href={row.href} target={row.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="font-display text-champagne text-lg md:text-xl text-right hover:text-gold transition-colors">
                      {row.value}
                    </a>
                  ) : (
                    <div className="font-display text-champagne text-lg md:text-xl text-right">{row.value}</div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- 9. Newsletter ---------- */
const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const { getSection } = useHomeContent();
  const n = getSection("newsletter", {
    kicker: "STAY IN THE WORLD OF ANABIYA",
    headline: "Receive our",
    headline_accent: " seasonal edit.",
    body: "New collections, design inspiration and trade offers — delivered occasionally.",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success("Welcome to the Anabiya world");
      setEmail("");
    } catch { toast.error("Please try again."); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="newsletter" className="py-20 md:py-24 bg-white border-t border-hairline">
      <div className="max-w-3xl mx-auto px-6 md:px-14 text-center">
        <div className="overline text-gold tracking-[4px] text-[11px] mb-3">{n.kicker}</div>
        <h2 className="font-display text-forest text-3xl md:text-5xl leading-tight tracking-tight">
          {n.headline}<span className="italic font-editorial text-champagne">{n.headline_accent}</span>
        </h2>
        <p className="mt-4 text-forest/60 text-sm font-light">{n.body}</p>
        <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email"
            data-testid="newsletter-email"
            className="flex-1 bg-transparent border border-hairline px-4 py-3 text-forest placeholder:text-forest/40 focus:outline-none focus:border-forest"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="newsletter-submit"
            className="inline-flex items-center justify-center gap-2 bg-forest text-champagne overline text-xs px-6 py-3 hover:bg-[hsl(150_60%_16%)] transition-colors tracking-[3px] disabled:opacity-40"
          >
            {busy ? "Sending…" : "Subscribe"}
          </button>
        </form>
      </div>
    </section>
  );
};

/* ---------- Trade drawer (light adapter around leads endpoint) ---------- */
const TradeDrawer = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", country: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/trade-enquiry`, form);
      toast.success("Thank you — our trade team will reach out within one business day.");
      setForm({ name: "", email: "", company: "", country: "", message: "" });
      onClose();
    } catch { toast.error("Could not send — try again in a moment."); }
    finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-forest/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4" onClick={onClose} data-testid="trade-drawer">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white w-full max-w-lg p-6 md:p-10 border border-hairline">
        <div className="overline text-gold tracking-[3px]">§ Trade enquiry</div>
        <h3 className="font-display text-forest text-3xl mt-2">Request the catalogue</h3>
        <div className="mt-6 space-y-3">
          {[
            { k: "name", ph: "Your name" },
            { k: "email", ph: "Business email", t: "email" },
            { k: "company", ph: "Company" },
            { k: "country", ph: "Country" },
          ].map(({ k, ph, t = "text" }) => (
            <input key={k} required type={t} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph} className="w-full border border-hairline px-3 py-2.5 text-forest focus:outline-none focus:border-forest" data-testid={`trade-${k}`} />
          ))}
          <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="What are you sourcing?" className="w-full border border-hairline px-3 py-2.5 text-forest focus:outline-none focus:border-forest" data-testid="trade-message" />
        </div>
        <div className="mt-5 flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} className="overline text-forest border border-hairline px-4 py-2.5 hover:border-forest">Cancel</button>
          <button type="submit" disabled={busy} className="bg-forest text-champagne overline px-5 py-2.5 tracking-[3px] disabled:opacity-40" data-testid="trade-submit">
            {busy ? "Sending…" : "Send enquiry"}
          </button>
        </div>
      </form>
    </div>
  );
};

/* ---------- Orchestration ---------- */
export const HomeReference = () => {
  useSmoothScroll();
  const [tradeOpen, setTradeOpen] = useState(false);
  const openTrade = () => setTradeOpen(true);

  return (
    <main data-testid="home-reference" className="bg-white text-forest min-h-screen">
      <Seo
        path="/"
        title="Anabiya Decor — Luxury Wholesale Bedding & Home Textiles, Crafted in India"
        description="Premium bedding, hotel linen, curtains and home textiles designed in Mumbai and shipped to 30+ countries. Trusted by hotels, retailers and interior designers worldwide."
      />
      <AnnouncementBar />
      <NavbarLuxe onOpenTrade={openTrade} />
      <SplitHero onOpenTrade={openTrade} />
      <CategoryTilesGrid />
      <Bestsellers />
      <Testimonials />
      <QualityValues />
      <StoryPreview />
      <Leadership />
      <StatsRow onOpenTrade={openTrade} />
      <CourierPartners />
      <ContactBlock />
      <TrustStrip />
      <B2BCta onOpenTrade={openTrade} />
      <Newsletter />
      <Footer />
      <FloatingButtons />
      <TradeDrawer open={tradeOpen} onClose={() => setTradeOpen(false)} />
    </main>
  );
};

export default HomeReference;
